import { getInteractor, getSemaphore } from "services/local.service";
import { KeyConstant, SystemConstant } from "const";
import { convertString2JSON, isE2E, StorageUtil, toCamel, toSnake } from "utils";
import { checkTriggerMessageUI } from "../../../../sagas/saga.helper";
import store, { ConversationActions } from "redux-store";
import { isNil } from "lodash";
import { arrayOf } from "prop-types";
import { normalizeError } from "utils/string.utils";

const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;

class DecryptNormalMessage {
  /**
   *
   * @param newMessage
   * @param group
   * @param messageOptions
   * @param {string} prefixKey
   * @param indexNumber
   * @returns {Promise<null|number>}
   */
  decryptNormalMessage = async (newMessage, group, messageOptions, prefixKey, indexNumber) => {
    const encryption_type =
      messageOptions?.encryption_f !== SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION
        ? SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION
        : SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION;
    let messageKey = "";
    let decryptContent = null;
    const encryptVersion = messageOptions.encrypt_version;
    const encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);
    let encryptStatusInfo = null;

    if (encryption_type === SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION) {
      try {
        let waitingNumber = await this.getWaitingIndex(prefixKey, group, newMessage, encryptTypeProtocol);
        let decrypted = await this.decryptMessage(
          newMessage.sendType,
          group.groupType,
          prefixKey,
          newMessage,
          indexNumber,
          encryptTypeProtocol,
        );
        decryptContent = decrypted.decryptContent;
        messageKey = decrypted.messageKey;
        let messageEncrypt = await this.makeMessageEncrypt(
          prefixKey,
          newMessage,
          indexNumber,
          encryptTypeProtocol,
          decryptContent,
          group,
          messageOptions,
          messageKey,
        );
        await getInteractor(prefixKey).LocalMessageEncryptService.save([messageEncrypt]);
        if (decryptContent) {
          newMessage.content = decryptContent;
          if (waitingNumber !== indexNumber && waitingNumber !== -1) {
            newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptOutOfIndex;
          } else {
            newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
          }
          encryptStatusInfo = {
            type: SystemConstant.ENCRYPT_STATUS_TYPE.success,
            code: SystemConstant.ENCRYPT_STATUS_CODE.success,
          };
        } else {
          newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover;
          encryptStatusInfo = {
            type: SystemConstant.ENCRYPT_STATUS_TYPE.error,
            code: SystemConstant.ENCRYPT_STATUS_CODE.errorCommon,
          };
        }
        return encryptStatusInfo;
      } catch (error) {
        return await this.handleErrorDecrypt(error, prefixKey, newMessage, group, encryptTypeProtocol);
      }
    }
    return null;
  };

  /**
   * @param prefixKey
   * @param newMessage
   * @param indexNumber
   * @param encryptTypeProtocol
   * @param decryptContent
   * @param group
   * @param messageOptions
   * @param messageKey
   * @returns {Promise<{session_data: (string|*), plain_text: *, send_to_account_id, created: number, success_number: *, content, sender_id: *, index_number: *, group_id, pre_index: (number|*), file_id: (string|*), send_to_device_id, options: string, sender_device_id: () => string, modified: number, id, source_id, message_key, error_number: *, waiting_number: *, key_exchange_algorithm: (string|*)}>}
   */
  makeMessageEncrypt = async (
    prefixKey,
    newMessage,
    indexNumber,
    encryptTypeProtocol,
    decryptContent,
    group,
    messageOptions,
    messageKey,
  ) => {
    const messageEncryptMetaData = await getInteractor(prefixKey).LocalCryptoService.createMessageEncryptionMetaData(
      newMessage.senderId,
      newMessage.senderDeviceId,
      decryptContent,
      newMessage.sendType,
      newMessage.groupId,
      indexNumber,
      group.groupType === SystemConstant.GROUP_CHAT_TYPE.personal,
      encryptTypeProtocol,
    );

    return {
      id: newMessage.id,
      source_id: newMessage.sourceId,
      send_to_account_id: newMessage.accountId,
      content: newMessage.content,
      group_id: newMessage.groupId,
      message_key: messageKey,
      session_data: messageEncryptMetaData.session_data,
      file_id: messageEncryptMetaData.file_id,
      key_exchange_algorithm: messageEncryptMetaData.key_exchange_algorithm,
      plain_text: messageEncryptMetaData.plain_text,
      send_to_device_id: newMessage.deviceId,
      options: JSON.stringify({ ...messageOptions, encrypt_version: encryptTypeProtocol }),
      created: Date.now(),
      modified: Date.now(),
      index_number: messageEncryptMetaData.index_number,
      waiting_number: messageEncryptMetaData.waiting_number,
      error_number: messageEncryptMetaData.error_number,
      success_number: messageEncryptMetaData.success_number,
      pre_index: messageEncryptMetaData.prev_number,
      sender_id: newMessage.senderId,
      sender_device_id: newMessage.senderDeviceId,
    };
  };

  /**
   * @param error
   * @param {string} prefixKey
   * @param {{}} errorObj
   * @param {{}} newMessage
   * @param {{}} group
   * @param {EncryptTypeProtocol} encryptTypeProtocol
   * @returns {Promise<number>}
   */
  handleErrorDecrypt = async (error, prefixKey, newMessage, group, encryptTypeProtocol) => {
    if (newMessage == null) return SystemConstant.ENCRYPT_STATUS_TYPE.error;
    const messageOptions = convertString2JSON(newMessage.options, {});
    let errorObj = error;
    // Nếu error.message chứa JSON, parse ra object
    if (typeof error.message === "string") {
      const match = error.message.match(/\{.*\}$/);
      if (match) {
        try {
          errorObj = JSON.parse(match[0]);
        } catch (e) {
          // Không parse được thì giữ nguyên errorObj
        }
      }
    }
    if (
      errorObj?.message &&
      errorObj?.message?.includes("missing sender key state for distribution ID") &&
      group.groupType !== SystemConstant.GROUP_CHAT_TYPE.personal
    ) {
      newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover;
      return {
        type: SystemConstant.ENCRYPT_STATUS_TYPE.errorDistribute,
        code: SystemConstant.ENCRYPT_STATUS_CODE.errorDistribution,
        message: normalizeError(error),
      };
    } else if (errorObj?.name === "DuplicatedMessage") {
      const exitsMessage = await getInteractor(prefixKey).LocalMessageService.findOneOrThrow({
        id: newMessage.id,
      });
      newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.duplicateDecrypt;
      if (isE2E(newMessage.sendType, group.groupType)) {
        let session = toCamel(
          await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
            newMessage.senderId,
            newMessage.senderDeviceId,
            newMessage.groupId,
            encryptTypeProtocol,
          ),
        );
        if (session.waitingIndex === messageOptions.index_send_number) {
          await getInteractor(prefixKey).LocalCryptoService.increaseWaitingIndexSession(
            newMessage.senderId,
            newMessage.senderDeviceId,
            newMessage.groupId,
            encryptTypeProtocol,
          );
        }
      } else {
        let sender = toCamel(
          await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
            newMessage.senderId,
            newMessage.senderDeviceId,
            newMessage.groupId,
            encryptTypeProtocol,
          ),
        );
        if (sender.waitingIndex === messageOptions.index_send_number) {
          await getInteractor(prefixKey).LocalCryptoService.increaseWaitingIndexSender(
            newMessage.senderId,
            newMessage.senderDeviceId,
            newMessage.groupId,
            encryptTypeProtocol,
          );
        }
      }
      if (exitsMessage && Object.keys(exitsMessage).length > 0) {
        let isValidMessage = await getInteractor(prefixKey).LocalCryptoService.isValidMessage(exitsMessage.content);
        let updateContent = exitsMessage.content;
        if (!isValidMessage) {
          const encryptExist = await getInteractor(prefixKey).LocalMessageEncryptService.findOneOrThrow({
            id: newMessage.id,
          });
          isValidMessage = await getInteractor(prefixKey).LocalCryptoService.isValidMessage(encryptExist.content);
          if (isValidMessage) {
            updateContent = encryptExist.content;
          }
        } else {
          updateContent = exitsMessage.content;
        }
        if (isValidMessage) {
          newMessage.content = updateContent;
          newMessage.state = SystemConstant.STATE.active;
          newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
          return {
            type: SystemConstant.ENCRYPT_STATUS_TYPE.success,
            code: SystemConstant.ENCRYPT_STATUS_CODE.successDuplicate01,
          };
        }
      }
      newMessage.state = SystemConstant.STATE.inactive;
      return {
        type: SystemConstant.ENCRYPT_STATUS_TYPE.success,
        code: SystemConstant.ENCRYPT_STATUS_CODE.successDuplicate03,
        message: normalizeError(error),
      };
    }
    newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover;
    return {
      type: SystemConstant.ENCRYPT_STATUS_TYPE.error,
      code: SystemConstant.ENCRYPT_STATUS_CODE.errorCommon,
      message: normalizeError(error),
    };
  };

  /**
   *
   * @param sendType
   * @param groupType
   * @param prefixKey
   * @param newMessage
   * @param indexNumber
   * @param encryptTypeProtocol
   * @returns {Promise<{messageKey: (Object[]|{}), decryptContent: (Promise<string|undefined>|Promise<string|null>|*)}>}
   */
  decryptMessage = async (sendType, groupType, prefixKey, newMessage, indexNumber, encryptTypeProtocol) => {
    const semaphore = getSemaphore(prefixKey);
    const [value, release] = await semaphore.acquire();
    try {
      let decrypted = {};
      if (isE2E(sendType, groupType)) {
        decrypted = await this.decryptE2EMessage(prefixKey, newMessage, indexNumber, encryptTypeProtocol);
      } else {
        decrypted = await this.decryptGroupMessage(prefixKey, newMessage, indexNumber, encryptTypeProtocol);
      }
      return decrypted;
    } finally {
      release();
    }
  };

  /**
   * @param {string} prefixKey
   * @param {{}} newMessage
   * @param {number || null} indexNumber
   * @param {EncryptTypeProtocol} encryptTypeProtocol
   * @returns {Promise<{messageKey: object[] | {}, decryptContent: (Promise<string|undefined>|Promise<string|null>|*)}>}
   */
  decryptGroupMessage = async (prefixKey, newMessage, indexNumber, encryptTypeProtocol) => {
    let decryptContent = await getInteractor(prefixKey).LocalCryptoService.decryptE2EEMessage(
      newMessage.senderId,
      newMessage.senderDeviceId,
      newMessage.groupId,
      newMessage.content,
      indexNumber,
      encryptTypeProtocol,
    );
    let messageKey = toCamel(
      await getInteractor(prefixKey).LocalCryptoService.getSenderMessageKey(
        newMessage.senderId,
        newMessage.senderDeviceId,
        newMessage.groupId,
        encryptTypeProtocol,
      ),
    );

    return {
      decryptContent,
      messageKey,
    };
  };

  /**
   * @param prefixKey
   * @param {{}} newMessage
   * @param {number || null} indexNumber
   * @param {EncryptTypeProtocol} encryptTypeProtocol
   * @returns {Promise<{messageKey: object[] | {}, decryptContent: (Promise<string|undefined>|Promise<string|null>|*)}>}
   */
  decryptE2EMessage = async (prefixKey, newMessage, indexNumber, encryptTypeProtocol) => {
    let decryptContent = await getInteractor(prefixKey).LocalCryptoService.decryptE2EMessage(
      newMessage.senderId,
      newMessage.senderDeviceId,
      newMessage.groupId,
      newMessage.content,
      indexNumber,
      encryptTypeProtocol,
    );
    let messageKey = await getInteractor(prefixKey).LocalCryptoService.getSessionReceiverMessageKey(
      newMessage.senderId,
      newMessage.senderDeviceId,
      newMessage.content,
      newMessage.groupId,
      encryptTypeProtocol,
    );
    return {
      decryptContent,
      messageKey,
    };
  };

  /**
   *
   * @param newMessage
   * @param group
   * @param messageOptions
   * @param prefixKey
   * @return {Promise<{isAllDone: boolean, lastMessage: null}>}
   */
  decryptRecoverMessages = async (newMessage, group, messageOptions, prefixKey) => {
    let lastMessage = null;

    if (!group) return { lastMessage, isAllDone: true };
    let encryptVersion = messageOptions.encrypt_version;
    let encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);
    let myDeviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
    let isAllDone = true;
    let currentIndexSend = await this.getWaitingIndex(prefixKey, group, newMessage, encryptTypeProtocol);
    if (!currentIndexSend) return { lastMessage, isAllDone: true };
    if (newMessage.senderDeviceId !== myDeviceId && myDeviceId) {
      let offset = 0;
      const limit = 500;
      do {
        let successMessages = [];
        let recoverMessages = await getInteractor(prefixKey).LocalMessageService.getDecryptRecoverMessage(
          newMessage.senderId,
          newMessage.senderDeviceId,
          newMessage.deviceId,
          newMessage.groupId,
          encryptVersion,
          [
            SystemConstant.SEND_TYPE.senderKey,
            SystemConstant.SEND_TYPE.keyError,
            SystemConstant.SEND_TYPE.encryptStatus,
          ],
          offset,
          limit,
        );
        if (recoverMessages.length === 0) break;
        offset += recoverMessages.length;
        let failedMessages = recoverMessages.filter(msg => {
          const msgOptions = convertString2JSON(msg.options, {});
          return !msgOptions?.index_send_number || msgOptions?.index_send_number < currentIndexSend;
        });

        for (const failedMessage of failedMessages) {
          await this.decryptFailedOneMessage(failedMessage, group, encryptTypeProtocol, prefixKey);
        }
        let failedMap = failedMessages.map(msg => msg.id);
        recoverMessages = recoverMessages.filter(msg => !failedMap.includes(msg.id));
        recoverMessages.sort((a, b) => {
          const aOptions = convertString2JSON(a.options, {})?.index_send_number;
          const bOptions = convertString2JSON(b.options, {})?.index_send_number;
          return aOptions - bOptions;
        });
        for (const recoverMessage of recoverMessages) {
          let lastMessage = await this.decryptRecoverOneMessage(recoverMessage, group, encryptTypeProtocol, prefixKey);
          if (lastMessage?.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.done) {
            successMessages.push(lastMessage);
          }
        }
        lastMessage = successMessages.length > 0 ? successMessages[successMessages.length - 1] : null;
        isAllDone = isAllDone && successMessages.length === recoverMessages.length;
      } while (offset % limit === 0);
    }
    return {
      lastMessage: lastMessage,
      isAllDone: isAllDone,
    };
  };

  /**
   *
   * @param prefixKey
   * @param group
   * @param newMessage
   * @param encryptTypeProtocol
   * @returns {Promise<*|number>}
   */
  getWaitingIndex = async (prefixKey, group, newMessage, encryptTypeProtocol) => {
    if (isNil(group?.groupType)) {
      throw new Error("cannot find group type");
    }
    if (group.groupType === SystemConstant.GROUP_CHAT_TYPE.personal) {
      let session = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
        newMessage.senderId,
        newMessage.senderDeviceId,
        newMessage.groupId,
        encryptTypeProtocol,
      );
      return session?.waiting_index ?? -1;
    } else {
      let sender = await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
        newMessage.senderId,
        newMessage.senderDeviceId,
        newMessage.groupId,
        encryptTypeProtocol,
      );
      return sender?.waiting_index ?? -1;
    }
  };

  /**
   *
   * @param prefixKey
   * @param group
   * @param newMessage
   * @param encryptTypeProtocol
   * @returns {Promise<*|number>}
   */
  getLastIndex = async (prefixKey, group, newMessage, encryptTypeProtocol) => {
    if (isNil(group?.groupType)) {
      throw new Error("cannot find group type");
    }
    if (group.groupType === SystemConstant.GROUP_CHAT_TYPE.personal) {
      let session = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
        newMessage.senderId,
        newMessage.senderDeviceId,
        newMessage.groupId,
        encryptTypeProtocol,
      );
      return session?.last_index ?? -1;
    } else {
      let sender = await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
        newMessage.senderId,
        newMessage.senderDeviceId,
        newMessage.groupId,
        encryptTypeProtocol,
      );
      return sender?.last_index ?? -1;
    }
  };

  /**
   *
   * @param {*} prefixKey
   * @param {*} message
   * @param {*} group
   * @param {*} encryptTypeProtocol
   * @param {*} currentWaitingIndex
   * @param {*} lastIndex
   * @returns {Promise<*|number>}
   */
  validateWaitingIndex = async (prefixKey, message, group, encryptTypeProtocol, currentWaitingIndex, lastIndex) => {
    if (isNil(group?.groupType)) {
      throw new Error("cannot find group type");
    }

    let validatedWaitingIndex = currentWaitingIndex;
    if (currentWaitingIndex > lastIndex) {
      return validatedWaitingIndex;
    }

    const ignoreCurrentMessageId = message.id;
    let existDecryptedMes = await getInteractor(prefixKey).LocalMessageService.getOneBySourceIdAndNotError(
      message.sourceId,
      ignoreCurrentMessageId,
      [SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover, SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptOutOfIndex],
    );
    if (existDecryptedMes == null) {
      return validatedWaitingIndex;
    }

    validatedWaitingIndex = currentWaitingIndex + 1;

    const updateValues = { waiting_index: validatedWaitingIndex };
    if (isE2E(message.sendType, group.groupType)) {
      await getInteractor(prefixKey).LocalCryptoService.updateSession(
        updateValues,
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        encryptTypeProtocol,
      );
    } else {
      await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
        updateValues,
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        encryptTypeProtocol,
      );
    }

    return validatedWaitingIndex;
  };

  /**
   *
   * @param message
   * @param group
   * @param encryptTypeProtocol
   * @param {string} prefixKey
   * @returns {Promise<null|*>}
   */
  decryptRecoverOneMessage = async (message, group, encryptTypeProtocol, prefixKey) => {
    const messageOptions = convertString2JSON(message.options, {});

    const indexNumber = messageOptions?.index_send_number;
    if (!indexNumber) return null;
    let decryptContent = null;
    let messageKey = null;
    let needRecoverMessageEncrypt = await getInteractor(prefixKey).LocalMessageEncryptService.findById(message.id);
    let needUpdateContent = message.content !== null && message.content !== "";
    let waitingIndex = await this.getWaitingIndex(prefixKey, group, message, encryptTypeProtocol);
    try {
      if (isE2E(message.sendType, group.groupType)) {
        if (message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptOutOfIndex) {
          if (waitingIndex === indexNumber) {
            await getInteractor(prefixKey).LocalCryptoService.increaseWaitingIndexSession(
              message.senderId,
              message.senderDeviceId,
              message.groupId,
              encryptTypeProtocol,
            );
          } else {
            return null;
          }
          message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
          return message;
        }
      } else {
        if (message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptOutOfIndex) {
          if (waitingIndex === indexNumber) {
            await getInteractor(prefixKey).LocalCryptoService.increaseWaitingIndexSender(
              message.senderId,
              message.senderDeviceId,
              message.groupId,
              encryptTypeProtocol,
            );
          } else {
            return null;
          }
          message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
          return message;
        }
      }

      let validateMessage = await this.validateDecryptedMessage(prefixKey, message);
      if (validateMessage) return validateMessage;
      let decrypted = await this.decryptMessage(
        message.sendType,
        group.groupType,
        prefixKey,
        message,
        indexNumber,
        encryptTypeProtocol,
      );
      decryptContent = decrypted.decryptContent;
      messageKey = decrypted.messageKey;

      if (!decryptContent) return null;
      if (needRecoverMessageEncrypt == null) {
        const messageEncrypt = this.makeMessageEncrypt(
          prefixKey,
          message,
          indexNumber,
          encryptTypeProtocol,
          decryptContent,
          group,
          messageOptions,
          messageKey,
        );
        await getInteractor(prefixKey).LocalMessageEncryptService.save([messageEncrypt]);
      } else {
        await getInteractor(prefixKey).LocalMessageEncryptService.update(
          { plain_text: decryptContent },
          { id: needRecoverMessageEncrypt.id },
        );
      }
      let updateMessage = {
        message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.done,
        modified: Date.now(),
      };
      if (needUpdateContent) {
        message.content = decryptContent;
        updateMessage.content = decryptContent;
      }
      message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
      message.modified = Date.now();
      await getInteractor(prefixKey).LocalMessageService.updateMessage(message.id, updateMessage);
      if (checkTriggerMessageUI(prefixKey, message)) {
        store.dispatch(ConversationActions.receivedRemoteMessage(toSnake(message)));
      }

      return message;
    } catch (e) {
      return null;
    }
  };

  /**
   *
   * @param prefixKey
   * @param message
   * @returns {Promise<null|*>}
   */
  validateDecryptedMessage = async (prefixKey, message) => {
    let isValidMessage = await getInteractor(prefixKey).LocalCryptoService.isValidMessage(message.content);
    if (isValidMessage) {
      await getInteractor(prefixKey).LocalMessageService.updateMessage(message.id, {
        message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.done,
        state: SystemConstant.STATE.active,
        modified: Date.now(),
      });
      message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
      message.state = SystemConstant.STATE.active;
      await getInteractor(prefixKey).LocalMessageEncryptService.LocalMessageEncryptService.update(
        { plain_text: message.content },
        {
          id: message.id,
        },
      );
      return message;
    }
    return null;
  };

  /**
   *
   * @param rootMessage
   * @param group
   * @param prefixKey
   * @param encryptTypeProtocol
   * @return {Promise<{isAllDone: boolean, lastMessage: null}>}
   */
  validateRecoverMessages = async (rootMessage, group, prefixKey, encryptTypeProtocol) => {
    let needRecover = null;
    try {
      if (!group) return { lastMessage: null, isAllDone: true };

      let waitingNumber;
      if (isE2E(rootMessage.sendType, group.groupType)) {
        const session = toCamel(
          await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
            rootMessage.senderId,
            rootMessage.senderDeviceId,
          ),
        );
        if (!session) return { lastMessage: null, isAllDone: false };
        waitingNumber = session.waitingIndex;
      } else {
        const sender = toCamel(
          await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
            rootMessage.senderId,
            rootMessage.senderDeviceId,
            group.id,
            encryptTypeProtocol,
          ),
        );
        if (!sender) return { lastMessage: null, isAllDone: false };
        waitingNumber = sender.waitingIndex;
      }
      let recoverMessages = await getInteractor(prefixKey).LocalMessageService.getDecryptRecoverMessageOne(
        rootMessage.senderId,
        rootMessage.senderDeviceId,
        rootMessage.deviceId,
        rootMessage.groupId,
        encryptTypeProtocol,
        [SystemConstant.SEND_TYPE.senderKey, SystemConstant.SEND_TYPE.keyError, SystemConstant.SEND_TYPE.encryptStatus],
        waitingNumber,
      );

      recoverMessages = recoverMessages.filter(msg => {
        const msgOptions = convertString2JSON(msg.options, {});
        const rootMessageOptions = convertString2JSON(rootMessage.options, {});
        return msgOptions?.encrypt_version === rootMessageOptions?.encrypt_version;
      });

      if (rootMessage.sendType === SystemConstant.SEND_TYPE.senderKey) {
        recoverMessages = recoverMessages.filter(msg => msg.send_type === SystemConstant.SEND_TYPE.senderKey);
      } else {
        recoverMessages = recoverMessages.filter(msg => msg.send_type !== SystemConstant.SEND_TYPE.senderKey);
      }

      needRecover = toCamel(
        recoverMessages.find(msg => {
          const msgOptions = convertString2JSON(msg.options, {});
          return msgOptions?.index_send_number === waitingNumber;
        }),
      );

      if (!needRecover) return { lastMessage: null, isAllDone: false };

      const needRecoverOptions = convertString2JSON(needRecover.options, {});
      const indexNumber = needRecoverOptions?.index_send_number;
      if (!indexNumber) return { lastMessage: null, isAllDone: false };

      let decryptContent = null;
      let messageKey = null;
      let decrypted = await this.decryptMessage(
        needRecover.sendType,
        group.groupType,
        prefixKey,
        needRecover,
        indexNumber,
        encryptTypeProtocol,
      );
      decryptContent = decrypted.decryptContent;
      messageKey = decrypted.messageKey;

      if (!decryptContent) return { lastMessage: null, isAllDone: false };

      const messageEncryptMetaData = await getInteractor(prefixKey).LocalCryptoService.createMessageEncryptionMetaData(
        needRecover.senderId,
        needRecover.senderDeviceId,
        decryptContent,
        needRecover.sendType,
        needRecover.groupId,
        indexNumber,
        group.groupType === SystemConstant.GROUP_CHAT_TYPE.personal,
        needRecoverOptions?.encrypt_version,
      );

      const messageEncrypt = {
        id: needRecover.id,
        source_id: needRecover.sourceId,
        send_to_account_id: needRecover.accountId,
        content: needRecover.content,
        group_id: needRecover.groupId,
        message_key: messageKey,
        session_data: messageEncryptMetaData.session_data,
        file_id: messageEncryptMetaData.file_id,
        key_exchange_algorithm: messageEncryptMetaData.key_exchange_algorithm,
        plain_text: messageEncryptMetaData.plain_text,
        send_to_device_id: needRecover.deviceId,
        options: JSON.stringify({ ...needRecoverOptions }),
        created: Date.now(),
        modified: Date.now(),
        index_number: messageEncryptMetaData.index_number,
        waiting_number: messageEncryptMetaData.waiting_number,
        error_number: messageEncryptMetaData.error_number,
        success_number: messageEncryptMetaData.success_number,
        pre_index: messageEncryptMetaData.prev_number,
        sender_id: needRecover.senderId,
        sender_device_id: needRecover.senderDeviceId,
      };

      await getInteractor(prefixKey).LocalMessageEncryptService.save([messageEncrypt]);

      await getInteractor(prefixKey).LocalMessageService.updateMessage(needRecover.id, {
        message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.done,
        content: decryptContent,
      });

      return await this.decryptRecoverMessages(needRecover, group, needRecoverOptions, prefixKey);
    } catch (error) {
      await this.handleErrorDecrypt(error, prefixKey, needRecover, group, encryptTypeProtocol);
      console.error("Error validating recover messages:", error);
      return { lastMessage: null, isAllDone: false };
    }
  };

  /**
   *
   * @param message
   * @param group
   * @param encryptTypeProtocol
   * @param prefixKey
   * @returns {Promise<null|object[]|{}>}
   */
  decryptFailedOneMessage = async (message, group, encryptTypeProtocol, prefixKey) => {
    try {
      message = toCamel(message);
      let decryptContent = null;
      let needRecoverMessageEncrypt = await getInteractor(prefixKey).LocalMessageEncryptService.findById(message.id);
      if (needRecoverMessageEncrypt == null) return;
      let needUpdateContent = message.content !== null && message.content !== "";
      if (message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptOutOfIndex) {
        let updateMessage = {};
        message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
        updateMessage.message_encrypt_status = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
        await getInteractor(prefixKey).LocalMessageService.updateMessage(message.id, updateMessage);
        return message;
      }
      let isValidMessage = await getInteractor(prefixKey).LocalCryptoService.isValidMessage(message.content);
      if (isValidMessage) {
        await getInteractor(prefixKey).LocalMessageService.updateMessage(message.id, {
          message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.done,
          state: SystemConstant.STATE.active,
          modified: Date.now(),
        });
        message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
        message.state = SystemConstant.STATE.active;
        if (needRecoverMessageEncrypt) {
          await getInteractor(prefixKey).LocalMessageEncryptService.LocalMessageEncryptService.update(
            { plain_text: message.content },
            {
              id: needRecoverMessageEncrypt.id,
            },
          );
        }
        return message;
      }
      let decrypted = await this.decryptMessage(
        message.sendType,
        group.groupType,
        prefixKey,
        message,
        null,
        encryptTypeProtocol,
      );
      decryptContent = decrypted.decryptContent;

      if (!decryptContent) {
        message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptFailed;
        await getInteractor(prefixKey).LocalMessageService.updateMessage(message.id, {
          message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptFailed,
          modified: Date.now(),
        });
        return null;
      }
      let updateMessage = {};
      if (needUpdateContent) {
        message.content = decryptContent;
        updateMessage.state = SystemConstant.STATE.active;
        message.state = SystemConstant.STATE.active;
        updateMessage.content = decryptContent;
      }
      await getInteractor(prefixKey).LocalMessageService.updateMessage(message.id, {
        ...updateMessage,
        message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.done,
        modified: Date.now(),
      });
      message.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;

      if (needRecoverMessageEncrypt) {
        await getInteractor(prefixKey).LocalMessageEncryptService.LocalMessageEncryptService.update(
          { plain_text: message.content },
          {
            id: needRecoverMessageEncrypt.id,
          },
        );
      } else {
        const messageOptions = convertString2JSON(message.options, {});
        const indexNumber = messageOptions?.index_send_number;
        const messageEncrypt = await this.makeMessageEncrypt(
          prefixKey,
          message,
          indexNumber,
          encryptTypeProtocol,
          decryptContent,
          group,
          messageOptions,
          decrypted.messageKey,
        );
        await getInteractor(prefixKey).LocalMessageEncryptService.LocalMessageEncryptService.save([messageEncrypt]);
      }

      if (checkTriggerMessageUI(prefixKey, message)) {
        store.dispatch(ConversationActions.receivedRemoteMessage(toSnake(message)));
      }

      return message;
    } catch (error) {
      return null;
    }
  };

  /**
   * decrypt try re-send message if exits if not success inactive it
   */
  /**
   *
   * @param rootMessage
   * @param group
   * @param encryptTypeProtocol
   * @param prefixKey
   * @returns {Promise<void>}
   */
  decryptFailedMessages = async (rootMessage, group, encryptTypeProtocol, prefixKey) => {
    let myDeviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
    if (rootMessage.senderDeviceId !== myDeviceId && myDeviceId) {
      let offset = 0;
      let limit = 500;
      do {
        let recoverMessages = await getInteractor(prefixKey).LocalMessageService.getDecryptRecoverMessage(
          rootMessage.senderId,
          rootMessage.senderDeviceId,
          rootMessage.deviceId,
          rootMessage.groupId,
          encryptTypeProtocol,
          [
            SystemConstant.SEND_TYPE.senderKey,
            SystemConstant.SEND_TYPE.keyError,
            SystemConstant.SEND_TYPE.encryptStatus,
          ],
          offset,
          limit,
        );
        if (recoverMessages.length === 0) break;
        offset += recoverMessages.length;

        for (const message of recoverMessages) {
          await this.decryptFailedOneMessage(message, group, encryptTypeProtocol, prefixKey);
        }
      } while (offset % limit === 0);
    }
  };
}

export default DecryptNormalMessage;
